import cx from 'clsx';
import * as React from 'react';
import { Divider } from '#components/divider';
import { TextWithHighlights } from '#components/text-with-highlights';
import { getBadgeType, ProductBadge } from './product-badge/index.ts';
import { AddonsGroup, type AddonsGroupProps } from './addons-group.tsx';
import { BenefitsGroup } from './benefits-group.tsx';
import styles from './price-plan.module.css';

export interface Trait {
  name: 'active' | 'popular' | 'free_trial' | 'recommended' | 'promo' | 'preselected';
  title: string;
}

interface Benefits {
  title: string;
  benefits: Benefit[];
}

interface Benefit {
  description: string;
  isIncluded: boolean;
}

interface PricePeriodProps {
  pricePeriod: string;
  recurrence: 'monthly' | 'annual';
  monthlyPrice?: string;
  isPanel?: boolean;
}

function PricePeriod({
  pricePeriod,
  recurrence,
  monthlyPrice,
  isPanel,
}: PricePeriodProps): React.ReactNode {
  return (
    <span
      className={cx(
        'body-standard',
        recurrence === 'annual' && monthlyPrice && styles.priceDescriptionBlock,
        isPanel && styles.panelPeriod
      )}
    >
      {pricePeriod}
    </span>
  );
}

export interface PricePlanBaseProps {
  title: string;
  traits: Trait[];
  price: string;
  pricePeriod: string;
  description: string;
  ctaDescription: string;
  mainButton: React.ReactNode;
  benefitsGroups: Benefits[];
  addons?: AddonsGroupProps | null;
  priceDescription?: string;
  offerDetails?: string;
  hasPromoInAnyProducts?: boolean;
  promoOfferPrice?: string;
  subDescription?: string;
  hidePromoHighlights?: boolean;
  hidePromoInAllProducts?: boolean;
  monthlyPrice?: string;
  subtitle?: string;
  isOnboardingVariant?: boolean;
  pricePrefix?: string;
  compact?: boolean;
  topHighlightedBenefits?: Benefits;
  isPanel?: boolean;
}

export interface MonthlyProps extends PricePlanBaseProps {
  recurrence: 'monthly';
  descriptionCta?: React.ReactNode;
  priceCta?: React.ReactNode;
}

export interface AnnualProps extends PricePlanBaseProps {
  recurrence: 'annual';
}

export type Props = MonthlyProps | AnnualProps;

export function PricePlan({
  title,
  traits,
  price,
  pricePrefix,
  pricePeriod,
  description,
  ctaDescription,
  mainButton,
  benefitsGroups,
  addons,
  hasPromoInAnyProducts,
  promoOfferPrice,
  subDescription,
  priceDescription,
  hidePromoHighlights,
  hidePromoInAllProducts,
  subtitle,
  isOnboardingVariant,
  compact,
  topHighlightedBenefits,
  isPanel,
  ...props
}: Props): React.ReactNode {
  const { type: badgeType } = getBadgeType(traits);
  const hasPromoOffer = Boolean(promoOfferPrice) && !hidePromoInAllProducts;
  const hasPromoOfferBadge = hasPromoOffer && badgeType === 'promo';
  const isPreselected = badgeType === 'preselected';
  const isPopular = badgeType === 'popular' || badgeType === 'recommended';
  const shouldHideTopPadding = isOnboardingVariant && !hasPromoInAnyProducts;

  const showStrikeThroughPrice =
    !compact &&
    props.recurrence === 'annual' &&
    Boolean(props.monthlyPrice) &&
    props.monthlyPrice !== price;
  return (
    <div
      className={cx(styles.container, compact && styles.compact)}
      data-test-has-promo-in-any-products={hasPromoInAnyProducts}
      data-test-has-promo-offer={hasPromoOffer}
    >
      <div
        className={cx(
          styles.cardContainer,
          hasPromoOfferBadge && !hidePromoHighlights && styles.hasPromoOffer,
          isPreselected && styles.preselected,
          compact && isPopular && styles.popular,
          isPanel && styles.panelCard
        )}
      >
        <div className={styles.titleContainer}>
          <div className={styles.titleTop}>
            <h2 className={cx(styles.title, 'heading-section')} data-test-card-title>
              {title}
            </h2>
            <ProductBadge
              traits={hidePromoHighlights ? traits.filter(t => t.name !== 'promo') : traits}
            />
          </div>
          {subtitle ? (
            <p className={cx('caption', styles.subtitle)} data-test-card-subtitle>
              {subtitle}
            </p>
          ) : null}
        </div>
        <div className={styles.priceSection} data-test-card-price>
          {hasPromoInAnyProducts ? (
            <div className={styles.priceContainer}>
              {promoOfferPrice ? (
                <>
                  <span className={cx(styles.strikethroughtPrice, 'heading-section')}>{price}</span>
                  <span className="heading-page">{promoOfferPrice}</span>
                </>
              ) : (
                <span className="heading-page">
                  {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx -- only empty space */}
                  {pricePrefix ? `${pricePrefix} ${price}` : price}
                </span>
              )}
              <PricePeriod
                monthlyPrice={props.recurrence === 'annual' ? props.monthlyPrice : undefined}
                pricePeriod={pricePeriod}
                recurrence={props.recurrence}
              />

              {promoOfferPrice && subDescription ? (
                <div className="body-standard" data-test-price-sub-description>
                  {subDescription}
                </div>
              ) : null}

              {props.recurrence === 'monthly' && props.priceCta ? (
                <div className="body-standard" data-test-price-cta>
                  {props.priceCta}
                </div>
              ) : null}

              {priceDescription ? (
                <p
                  className={cx('body-standard', styles.billingRecurrence)}
                  data-test-card-billing-recurrence
                >
                  {priceDescription}
                </p>
              ) : null}
            </div>
          ) : (
            <>
              {showStrikeThroughPrice ? (
                <span
                  className={cx(
                    styles.strikethroughtPrice,
                    isPanel ? 'body-prominent' : 'heading-section'
                  )}
                >
                  {props.monthlyPrice}
                </span>
              ) : null}
              <span className={isPanel ? 'heading-section' : 'heading-page'}>
                {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx -- only empty space */}
                {pricePrefix ? `${pricePrefix} ${price}` : price}
              </span>
              <PricePeriod
                isPanel={isPanel}
                monthlyPrice={props.recurrence === 'annual' ? props.monthlyPrice : undefined}
                pricePeriod={pricePeriod}
                recurrence={props.recurrence}
              />

              {subDescription ? (
                <div
                  className={cx('body-standard', styles.priceSubDescription)}
                  data-test-price-sub-description
                >
                  {subDescription}
                </div>
              ) : null}

              {priceDescription ? (
                <div
                  className={cx('body-standard', styles.priceDescription)}
                  data-test-price-description
                >
                  {priceDescription}
                </div>
              ) : null}

              {props.recurrence === 'monthly' && props.priceCta ? (
                <div className="body-standard" data-test-price-cta>
                  {props.priceCta}
                </div>
              ) : null}

              {props.recurrence === 'monthly' && props.descriptionCta ? (
                <div className="body-standard" data-test-description-cta>
                  {props.descriptionCta}
                </div>
              ) : null}
            </>
          )}
        </div>
        {props.offerDetails ? (
          <div className={styles.priceDescriptionSection} data-test-card-price-description-text>
            <div className="body-standard" data-test-offer-details>
              {props.offerDetails}
            </div>
          </div>
        ) : null}
        {!compact ? (
          <p
            className={cx(styles.description, 'body-standard', isPanel && styles.panelDescription)}
            data-test-card-description
          >
            <TextWithHighlights isHighlighted={false}>{description}</TextWithHighlights>
          </p>
        ) : null}
        <div className={styles.ctaContainer}>
          {mainButton}
          {ctaDescription ? (
            <p
              className={cx('body-standard', styles.ctaDescription)}
              data-test-card-billing-recurrence
            >
              {ctaDescription}
            </p>
          ) : null}
        </div>
        {compact ? (
          <ul className={styles.compactBenefitsList} data-test-compact-benefits>
            {(
              topHighlightedBenefits?.benefits ??
              benefitsGroups[0]?.benefits.filter(b => b.isIncluded) ??
              []
            ).map(benefit => (
              <li
                className={cx(styles.compactBenefitItem, 'body-standard')}
                key={benefit.description}
              >
                <TextWithHighlights isHighlighted={false}>{benefit.description}</TextWithHighlights>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div
        className={cx(
          styles.benefitsContainer,
          (shouldHideTopPadding || isPanel) && styles.benefitsContainerWithoutTopPadding
        )}
      >
        {!compact ? (
          <>
            <div className={styles.benefitsGroups}>
              {benefitsGroups.map(({ title: groupTitle, benefits }) => (
                <BenefitsGroup benefits={benefits} key={groupTitle} title={groupTitle} />
              ))}
            </div>
            {addons ? (
              <div className={styles.bottomSection}>
                <Divider />
                <AddonsGroup addons={addons.addons} title={addons.title} />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
